<template>
  <b-card no-body>
    <b-card-header>
      <b-card-title>{{ $t(titleVariable) }}</b-card-title>
    </b-card-header>

    <b-card-body>
      <chartjs-component-bubble-chart
        :height="500"
        :data="this.series"
        :options="bubbleChart.options"
      />
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardHeader, BCardBody, BCardTitle } from 'bootstrap-vue'
import ChartjsComponentBubbleChart from '@/views/habit/charts-components/ChartjsComponentBubbleChart.vue'
import { chartColors, customHeatMapRiskMatrix, standardHeatMapRiskMatrix } from '@/constants'
import { $themeColors } from '@themeConfig'
import Chart from 'chart.js'
import i18n from '@/libs/i18n'

function mixColor(c1, c2, t) {
  const r = Math.round(c1[0] + (c2[0] - c1[0]) * t)
  const g = Math.round(c1[1] + (c2[1] - c1[1]) * t)
  const b = Math.round(c1[2] + (c2[2] - c1[2]) * t)
  return `rgb(${r},${g},${b})`
}

function getRiskColor(t) {
  // Verde → Amarillo → Rojo (para mapa estándar Adoval)
  if (t < 0.5) {
    const k = t * 2
    return mixColor([0, 200, 0], [255, 230, 0], k)
  } else {
    const k = (t - 0.5) * 2
    return mixColor([255, 230, 0], [220, 0, 0], k)
  }
}

function getRiskColorLV(row, col) {
  // Función adaptada para mapa LV - basada en valor de riesgo
  // row: 0-4 (impacto 1-5), col: 0-4 (probabilidad 1-5)

  // Calcular el valor de riesgo (impacto * probabilidad)
  const riskValue = (row + 1) * (col + 1)

  // Colores base (mismos que el mapa estándar para consistencia)
  const verde = [0, 200, 0]
  const amarillo = [255, 230, 0]
  const naranja = [255, 165, 0]
  const rojo = [220, 0, 0]

  if (riskValue < 4) {
    // 1 a 3.99: Verde a amarillo (transición suave como el mapa estándar)
    const k = (riskValue - 1) / 3
    return mixColor(verde, amarillo, k)
  } else if (riskValue < 8) {
    // 4 a 7.99: Amarillo a naranja
    const k = (riskValue - 4) / 4
    return mixColor(amarillo, naranja, k)
  } else {
    // 8 a 25: Naranja a rojo
    const k = Math.min((riskValue - 8) / 17, 1)
    return mixColor(naranja, rojo, k)
  }
}

// Plugin para agregar etiquetas a los cuadrantes
Chart.plugins.register({
  // Cuadrantes
  beforeDraw(chart) {
    if (!chart.config.options.quadrantLabels) return

    const ctx = chart.ctx
    const xScale = chart.scales['x-axis-0']
    const yScale = chart.scales['y-axis-0']
    const useCustomHeatMap = chart.config.options.useCustomHeatMap || false

    // ============================
    // Fondo degradé de cuadrantes (DETRÁS de los puntos)
    // ============================
    for (let col = 0; col < 5; col++) {
      for (let row = 0; row < 5; row++) {
        const x1 = xScale.getPixelForValue(col + 1)
        const x2 = xScale.getPixelForValue(col + 2)

        const y1 = yScale.getPixelForValue(row + 1)
        const y2 = yScale.getPixelForValue(row + 2)

        let color
        if (useCustomHeatMap) {
          // Usar función LV para mapa personalizado
          color = getRiskColorLV(row, col)
        } else {
          // Usar función estándar para mapa Adoval
          const risk = (row + col) / 8
          color = getRiskColor(risk)
        }

        ctx.fillStyle = color
        ctx.fillRect(x1, y2, x2 - x1, y1 - y2)
      }
    }
  },

  // Labels
  afterDraw(chart) {
    if (!chart.config.options.quadrantLabels) return

    const ctx = chart.ctx
    const xScale = chart.scales['x-axis-0']
    const yScale = chart.scales['y-axis-0']

    const xLabels = ['Improbable', 'Baja Probabilidad', 'Posible', 'Probable', 'Casi Seguro']
    const yLabels = ['Muy Bajo', 'Bajo', 'Medio', 'Alto', 'Muy Alto']

    ctx.save()
    ctx.fillStyle = '#666'
    ctx.font = '14px Arial'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    // Columnas
    for (let i = 0; i < 5; i++) {
      const x =
        (xScale.getPixelForValue(i + 1) +
          xScale.getPixelForValue(i + 2)) / 2

      ctx.fillText(xLabels[i], x, yScale.bottom + 18)
    }

    // Filas
    ctx.textAlign = 'right'
    for (let i = 0; i < 5; i++) {
      const y =
        (yScale.getPixelForValue(i + 1) +
          yScale.getPixelForValue(i + 2)) / 2

      ctx.fillText(yLabels[i], xScale.left - 8, y)
    }

    // Texto Impacto / Pbb
    ctx.font = 'bold 16px Arial'
    ctx.fillStyle = '#444'
    ctx.textAlign = 'left'
    ctx.textBaseline = 'bottom'
    ctx.fillText('Impacto / Pbb', xScale.left - 90, yScale.bottom + 27)

    ctx.restore()
  },

  // Etiquetas de los puntos
  afterDatasetsDraw(chart) {
    if (!chart.config.options.quadrantLabels) return

    const ctx = chart.ctx
    const datasets = chart.data.datasets
    const padding = 2

    ctx.save()
    ctx.font = 'bold 11px Arial'
    ctx.fillStyle = '#000'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'bottom'

    datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex)
      if (!meta || !meta.data) return

      const risk = dataset.riskRef
      if (!risk || !risk.code) return

      const text = risk.code
      const width = ctx.measureText(text).width

      meta.data.forEach((element) => {
        const model = element._model
        if (!model) return

        ctx.fillStyle = 'rgba(255,255,255)'
        ctx.fillRect(model.x - width / 2 - padding, model.y - 20, width + padding * 2, 14)

        ctx.fillStyle = '#000'
        ctx.fillText(text, model.x, model.y - 8)
      })
    })

    ctx.restore()
  }
})

export default {
  components: {
    ChartjsComponentBubbleChart,
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
  },
  props: {
    riskData: {
      type: Array,
      required: true,
    },
    titleVariable: {
      type: String,
      required: true,
    },
  },
  data() {
    const { customHeatMap } = JSON.parse(localStorage.getItem('clientData') || '{}')

    return {
      bubbleChart: {
        options: {
          quadrantLabels: true,     // Activar el plugin de etiquetas de cuadrantes
          useCustomHeatMap: customHeatMap || false,  // Usar mapa personalizado si el cliente lo tiene
          layout: {
            padding: {
              left: 70,   // 👈 espacio para las etiquetas de Impacto
            },
          },
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [
              {
                // display: true,
                gridLines: {
                  drawTicks: false,
                  drawBorder: false
                },
                ticks: {
                  display: false,
                  stepSize: 1,
                  min: 1,
                  max: 6.001,
                  fontColor: chartColors.labelColor,
                },
                scaleLabel: {
                  display: true,
                  // labelString: 'Probabilidad',
                  // fontSize: 16,
                  // fontStyle: 'bold',
                  // fontColor: '#222',
                  padding: { top: 20 },
                },
              },
            ],
            yAxes: [
              {
                display: true,
                gridLines: {
                  drawTicks: false,
                  drawBorder: false
                },
                ticks: {
                  display: false,
                  stepSize: 1,
                  min: 1,
                  max: 6,
                  fontColor: chartColors.labelColor,
                },
                scaleLabel: {
                  display: true,
                  // labelString: 'Impacto',
                  // fontSize: 16,
                  // fontStyle: 'bold',
                  // fontColor: '#222',
                  // padding: { bottom: 80 },
                }
              },
            ],
          },
          legend: {
            display: false,
          },
          tooltips: {
            enabled: true,
            mode: 'nearest',
            intersect: true,
            displayColors: false, // 👈 elimina los cuadraditos
            callbacks: {
              title(tooltipItems, data) {
                const item = tooltipItems[0]
                const dataset = data.datasets[item.datasetIndex]
                return dataset.riskRef?.name || ''
              },

              label(tooltipItem, data) {
                const dataset = data.datasets[tooltipItem.datasetIndex]
                const risk = dataset.riskRef
                if (!risk) return ''

                return [
                  `${i18n.t('label.code')}: ${risk.code}`,
                  `${i18n.t('label.impact')}: ${i18n.t(`residualRisk.${risk.impact}`)}`,
                  `${i18n.t('label.probability')}: ${i18n.t(`residualRisk.${risk.probability}`)}`,
                ]
              },
            },

            backgroundColor: '#fff',
            titleFontColor: '#000',
            bodyFontColor: '#000',
            borderColor: '#ccc',
            borderWidth: 1,
          },
        },
      },
    }
  },
  computed: {
    series() {
      return {
        animation: {
          duration: 10000,
        },
        datasets: this.riskData?.length
          ? this.riskData.map((risk) => ({
              backgroundColor: '#000000',
              borderColor: '#000000',
              riskRef: risk,   // 👈 guardamos el objeto completo
              data: [
                {
                  x: risk.x,
                  y: risk.y,
                  r: 5,
                },
              ],
            }))
          : []
      }
    },
  },
}
</script>
